import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CircularProgress, IconButton, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Table from "examples/Tables/Table";
import toast from "react-hot-toast";
import apiService from "services/api";
import { getAuthUserId } from "utils/authStorage";

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function Logs() {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);

  const fetchLogs = useCallback(async () => {
    const userId = getAuthUserId();
    if (!userId) {
      toast.error("Not signed in");
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.getLogs(userId);
      const result = response?.data;
      if (!result?.success) {
        throw new Error(result?.error || "Failed to fetch logs");
      }
      setLogs(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || error?.message || "Failed to fetch logs"
      );
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const table = useMemo(() => {
    const columns = [
      { name: "date", align: "left" },
      { name: "action", align: "left" },
      { name: "type", align: "left" },
      { name: "description", align: "left" },
    ];

    const rows = logs.map((log) => ({
      date: (
        <SoftTypography variant="caption" color="text">
          {formatDate(log.createdAt)}
        </SoftTypography>
      ),
      action: (
        <SoftTypography variant="button" fontWeight="medium">
          {log.action || "—"}
        </SoftTypography>
      ),
      type: (
        <SoftTypography variant="caption" color="secondary">
          {log.type || "—"}
        </SoftTypography>
      ),
      description: (
        <SoftTypography variant="caption" color="text">
          {log.description || "—"}
        </SoftTypography>
      ),
    }));

    return { columns, rows };
  }, [logs]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <SoftBox>
            <SoftTypography variant="h5" fontWeight="bold">
              Logs
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              Recent application activity (last 10 entries)
            </SoftTypography>
          </SoftBox>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={fetchLogs} disabled={loading} size="small">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </SoftBox>

        <Card>
          <SoftBox p={2}>
            <SoftBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <SoftBox display="flex" justifyContent="center" py={6}>
                  <CircularProgress size={32} />
                </SoftBox>
              ) : table.rows.length === 0 ? (
                <SoftBox py={6} textAlign="center">
                  <SoftTypography variant="body2" color="text">
                    No logs found.
                  </SoftTypography>
                </SoftBox>
              ) : (
                <Table columns={table.columns} rows={table.rows} />
              )}
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default Logs;
